import { FormProvider, useForm } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import dayjs, { Dayjs } from 'dayjs';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { setModalId, usePublicState } from '@/store/slices/publicSlice';
import {
  setSubscriptionFilters,
  useSubscriptionFilters,
} from '@/store/slices/reportFiltersSlice';
import CustomModal from '@/components/atoms/CustomModal';
import InfoText from '@/components/atoms/InfoText';
import FormField from '@/components/FormField/FormField';
import Button from '@/components/atoms/Button';
import DatesFilters from '../SharedComponents/DatesFilters';
import useDefaultReportDates from '../useDefaultReportDates';
import { useSharedFunctions } from '../SharedComponents/useSharedFunctions';
import {
  useGetSubscriptionPeriodTypesLKQuery,
  useGetSubscriptionPlansLKQuery,
} from '@/store/services/reports';

interface FormValues {
  periodType?: string[];
  planType?: string[];
  fromDate: Dayjs | null;
  toDate: Dayjs | null;
}

export default function FilterModal() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { modalId } = usePublicState();
  const filters = useSubscriptionFilters();
  const { defaultFromDate, defaultToDate } = useDefaultReportDates();
  const { resetPage } = useSharedFunctions();

  const { data: periodTypesData, isLoading: isPeriodTypesLoading } =
    useGetSubscriptionPeriodTypesLKQuery();
  const { data: plansData, isLoading: isPlansLoading } =
    useGetSubscriptionPlansLKQuery();

  const schema = z
    .object({
      periodType: z.array(z.string()).optional(),
      planType: z.array(z.string()).optional(),
      fromDate: z.custom<Dayjs>().nullable(),
      toDate: z.custom<Dayjs>().nullable(),
    })
    .superRefine(({ fromDate, toDate }, ctx) => {
      if (fromDate && toDate && toDate.isBefore(fromDate, 'day')) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: t(
            'dispute.dateToBeforeFrom',
            'Date to must be after date from'
          ),
          path: ['toDate'],
        });
      }
    });

  const methods = useForm<FormValues>({
    resolver: zodResolver(schema),
    mode: 'all',
    values: {
      periodType: Array.isArray(filters.periodType)
        ? filters.periodType
        : filters.periodType
          ? [filters.periodType]
          : [],
      planType: Array.isArray(filters.planType)
        ? filters.planType
        : filters.planType
          ? [filters.planType]
          : [],
      fromDate: filters.fromDate ? dayjs(filters.fromDate) : null,
      toDate: filters.toDate ? dayjs(filters.toDate) : null,
    },
  });

  const onClose = () => dispatch(setModalId(null));

  const isModalOpen = modalId === 'SUBSCRIPTION_REPORT_MODAL';

  const watchAll = methods.watch();

  const appliedFiltersCount = [
    !!watchAll.periodType?.length,
    !!watchAll.planType?.length,
    watchAll.fromDate,
    watchAll.toDate,
  ].filter(Boolean).length;

  const onReset = () => {
    methods.reset({
      periodType: [],
      planType: [],
      fromDate: defaultFromDate,
      toDate: defaultToDate,
    });
  };

  const onSubmit = (values: FormValues) => {
    const fromDateStr = values.fromDate
      ? values.fromDate.format('YYYY-MM-DD')
      : '';
    const toDateStr = values.toDate ? values.toDate.format('YYYY-MM-DD') : '';

    dispatch(
      setSubscriptionFilters({
        periodType: values.periodType || [],
        planType: values.planType || [],
        fromDate: fromDateStr,
        toDate: toDateStr,
      })
    );

    resetPage();
    onClose();
  };

  const periodTypesList = Array.isArray(periodTypesData)
    ? periodTypesData
    : (periodTypesData as any)?.payload || [];

  const periodTypeOptions =
    periodTypesList?.map((item: any) => ({
      label: item.name,
      value: item.code,
    })) || [];

  const plansList = Array.isArray(plansData)
    ? plansData
    : (plansData as any)?.payload || [];

  const planOptions =
    plansList?.map((item: any) => ({
      label: item.name,
      value: item.code,
    })) || [];

  return (
    <CustomModal
      title={t('home.filters', 'Filter')}
      isOpen={isModalOpen}
      onClose={onClose}
      subTitle={
        <div className="flex items-center gap-2">
          <InfoText
            size="sm"
            text={`${appliedFiltersCount} ${t('home.filterApplied', 'filter applied')}`}
          />
          <button
            type="button"
            onClick={onReset}
            className="text-light-success hover:underline text-sm font-semibold"
          >
            {t('home.resetFilter', 'Reset filter')}
          </button>
        </div>
      }
    >
      <FormProvider {...methods}>
        <form onSubmit={methods.handleSubmit(onSubmit)} className="space-y-6">
          {/* Period Type Section */}
          <FormField
            name="periodType"
            id="periodType"
            type="select"
            label={t('reports.periodType', 'Period type')}
            placeholder={t('home.select', 'select')}
            options={periodTypeOptions}
            loading={isPeriodTypesLoading}
            mode="multiple"
            allowClear
          />

          <hr className="border-t border-[#F0F0F0]" />

          {/* Plan Type Section */}
          <FormField
            name="planType"
            id="planType"
            type="checkBox"
            label={t('reports.planType', 'Plan type')}
            options={planOptions}
            disabled={isPlansLoading}
            className="flex items-center justify-between"
          />

          <hr className="border-t border-[#F0F0F0]" />

          {/* Date Range Section */}
          <DatesFilters />

          {/* Actions */}
          <div className="flex items-center justify-end gap-x-4 pt-4">
            <Button
              variant="outline"
              onClick={onClose}
              className="min-w-[120px] h-[48px]"
            >
              {t('home.cancel', 'Cancel')}
            </Button>
            <Button
              type="submit"
              variant="dark"
              className="min-w-[120px] h-[48px]"
            >
              {t('home.save', 'Save')}
            </Button>
          </div>
        </form>
      </FormProvider>
    </CustomModal>
  );
}
